import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

// Import Ionic
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonIcon,
  IonLoading,
  IonRow,
  useIonModal,
} from "@ionic/react";

// Import Styles
import classNames from "classnames/bind";
import styles from "./my-order-detail.module.scss";

// Import custom components
import { MyOrderProduct } from "../../../components/MyOrderProduct/MyOrderProduct";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { useGetOrderByIdQuery } from "../../../providers/Orders";
import { reload, starHalf, starSharp } from "ionicons/icons";
import { useCartData } from "../../../providers/cartDataStore";
import { useGetIncludeProductsQuery } from "../../../providers/products";
import { useConfig } from "../../../providers/config";
import OrderReview, { OrderReviewProps } from "../../../components/OrderReview";
import useReviewSetOrderById from "../../../providers/Orders/useGetReviewSerOrderById";
import Page from "../../page-layout";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { formatCurrency } from "../../../utils/currencyUtils";
import { Payment } from "../../../4time/src/entities";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

const MyOrderDetailFortime: React.FC = (props: any) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const { data: order, isLoading: loadingOrderDetail } = useGetOrderByIdQuery(props.match.params.id);

  const {
    data: orderReviewSet,
    isLoading: loadingOrderReviewSet,
    isRefetching,
    isError: isOrderReviewError,
  } = useReviewSetOrderById(String(order?.id), {
    enabled: !!order?.id,
  });

  const config = useConfig();

  const { orderDetailPage } = config ?? {};

  const { activeShippingZone } = useActiveOrMainShippingZone();

  const { data: products } = useGetIncludeProductsQuery({
    params: {
      zoneId: activeShippingZone.id,
    },
    options: {
      enabled: !!order?.lineItems?.length && !!activeShippingZone.id,
    },
    include: order?.lineItems?.map((product) => product.productId + "")!,
  });

  const { repeatOrder } = useCartData();

  const [presentModalRateOrder, dismissModalOrderReview] = useIonModal(OrderReview, {
    orderNumber: String(order?.id),
    onDismiss: () => dismissModalOrderReview(),
  } as OrderReviewProps);

  useEffect(() => {
    if (order && config?.isPaymentsEnabled) {
      setShowPaymentForm(order.needsPayment ?? false);
    }
  }, [order, config?.isPaymentsEnabled]);

  const getSingleProductDetail = (id: number | string) => {
    history.push("/product-detail/" + id);
  };

  const addCurrency = (v: any) => {
    if (Number.isNaN(v)) {
      return;
    }
    return formatCurrency(v, order?.currencySymbol!);
  };

  const handleRepeatOrder = () => {
    if (!products?.length || !order || !order.lineItems || !order.lineItems.length) {
      return;
    }

    repeatOrder(products, order.lineItems);
    history.push("/cart");
  };

  const handleShowOrderReview = () => {
    presentModalRateOrder();
  };

  const isLoading = loadingOrderDetail || loadingOrderReviewSet || isRefetching;

  return (
    <Page>
      <Page.HeaderMobile showBackButton showSearchButton />
      <Page.Content>
        <Page.HeaderDesktop />
        <IonLoading isOpen={isLoading} message={t("Please wait...")} duration={10000} />
        <div className={`${cx("my-order-detail")} global-content`}>
          {!loadingOrderDetail && (
            <MaxWidthContainer maxWidthBreakpoint={"Md"}>
              <IonCard className={cx("order-details")}>
                <IonCardHeader className={cx("order-details-header")} color="primary">
                  <IonCardTitle className={cx("order-details-title")}>{t("Order Detail")}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent
                  className={cx(
                    "order-details-content",
                    "ion-padding ion-margin-top",
                    (orderDetailPage?.repeatOrderEnabled || orderDetailPage?.reviewOrderEnabled) && "mt-0",
                  )}
                >
                  {showPaymentForm ? (
                    <Payment
                      orderId={String(order?.id)}
                      price={Number(order?.total)}
                      currencySymbol={order?.currencySymbol || "₽"}
                    />
                  ) : (
                    (orderDetailPage?.repeatOrderEnabled || orderDetailPage?.reviewOrderEnabled) && (
                      <div className="flex items-center h-full gap-2 text-sm py-1 px-[22px]">
                        {orderDetailPage?.repeatOrderEnabled && (
                          <div
                            onClick={handleRepeatOrder}
                            className={cx(
                              "repeat",
                              "flex items-center cursor-pointer rounded-xl p-2 hover:underline active:underline",
                            )}
                          >
                            <IonIcon icon={reload} className={cx("repeatIcon")} />
                            <div className="ml-2 text-black">{t("Repeat order")}</div>
                          </div>
                        )}
                        {orderDetailPage?.reviewOrderEnabled &&
                          !isOrderReviewError &&
                          order?.status === "completed" && (
                            <>
                              {!orderReviewSet?.productQuality ? (
                                <div
                                  onClick={handleShowOrderReview}
                                  className={cx(
                                    "rate",
                                    "rounded-xl flex items-center cursor-pointer p-2 hover:underline active:underline",
                                  )}
                                  color="primary"
                                >
                                  <IonIcon icon={starHalf} className={cx("rateIcon")} />
                                  <div className="ml-2 text-black">{t("Order review")}</div>
                                </div>
                              ) : (
                                <div className={cx("rate", "flex items-center")}>
                                  <div>
                                    {t("Your Rating")}: {orderReviewSet?.productQuality}
                                  </div>
                                  <IonIcon icon={starSharp} className={cx("rateIcon", "ml-2 text-red-600")} />
                                </div>
                              )}
                            </>
                          )}
                      </div>
                    )
                  )}
                  <IonCardSubtitle className={cx("order-details-subtitle")}>{t("Shipping Address")}</IonCardSubtitle>
                  <p className={cx("order-details-description")}>
                    {!order?.address?.house ? (
                      <>
                        <span>{`${t("Address")}: `}</span>
                        {order?.address?.fullAddress}
                        <br />
                      </>
                    ) : (
                      <>
                        <span>{`${t("City")}: `}</span>
                        {order?.address.city}
                        <br />
                        <span>{`${t("Street")}: `}</span>
                        {order?.address.street}
                        <br />
                        <span>{`${t("House number")}: `}</span>
                        {order?.address.house}
                        <br />
                        {order?.address.isFlat && (
                          <>
                            <span>{`${t("Entrance")}: `}</span>
                            {order?.address.entrance}
                            <br />
                            {!!order?.address.intercom && (
                              <>
                                <span>{`${t("Intercom code")}: `}</span>
                                {order?.address.intercom}
                                <br />
                              </>
                            )}
                            <span>{`${t("Floor")}: `}</span>
                            {order?.address.floor}
                            <br />
                            <span>{`${t("Apartment number")}: `}</span>
                            {order?.address.apartmentNumber}
                          </>
                        )}
                      </>
                    )}
                  </p>

                  {order?.deliveryTime && order.deliveryDate && (
                    <>
                      <IonCardSubtitle className={cx("order-details-subtitle")}>
                        {t("Delivery date and interval")}
                      </IonCardSubtitle>
                      <p className={cx("order-details-description")}>
                        <span>{t("Date")}: </span>
                        {order?.deliveryDate}
                        <br />
                        <span>Время: </span>
                        {order?.deliveryTime}
                      </p>
                    </>
                  )}
                  {order?.shippingLine?.methodTitle && (
                    <>
                      <IonCardSubtitle className={cx("order-details-subtitle")}>{t("Shipping Method")}</IonCardSubtitle>
                      <p className={cx("order-details-description")}>{order.shippingLine.methodTitle}</p>
                    </>
                  )}

                  {order?.paymentMethod?.title && (
                    <>
                      <IonCardSubtitle className={cx("order-details-subtitle")}>{t("Payment Method")}</IonCardSubtitle>
                      <p className={cx("order-details-description")}>{order.paymentMethod.title}</p>
                    </>
                  )}
                </IonCardContent>
              </IonCard>
              <IonCard className={cx("products-wrapper")}>
                <IonCardHeader className={cx("order-details-header")} color="primary">
                  <IonCardTitle className={cx("order-details-title")}>{t("Products")}</IonCardTitle>
                </IonCardHeader>

                <IonCardContent className={cx("order-details-content")}>
                  {order?.lineItems !== undefined &&
                    order.lineItems.map((item) => (
                      <MyOrderProduct
                        onClick={() => item.id && getSingleProductDetail(item.id)}
                        key={item.id}
                        item={item}
                        currencySymbol={order.currencySymbol ?? "₽"}
                      />
                    ))}
                </IonCardContent>
              </IonCard>
              <IonCard>
                <IonCardHeader color="primary" className={cx("order-details-header")}>
                  <IonCardTitle className={cx("order-details-title")}>{t("Total")}</IonCardTitle>
                </IonCardHeader>

                <IonCardContent className={cx("ion-padding ion-margin-top", "order-details-description")}>
                  {addCurrency(+order?.total! + +order?.discountTotal!) && (
                    <IonRow>
                      <IonCol size="6">{t("Products Price")}</IonCol>
                      <IonCol className="ion-text-right" size="6">
                        {addCurrency(+order?.total! + +order?.discountTotal!)}
                      </IonCol>
                    </IonRow>
                  )}

                  {addCurrency(+order?.shippingTotal!) && order?.shippingLine !== undefined && (
                    <IonRow>
                      <IonCol size="6">{t("Shipping Cost")}</IonCol>
                      <IonCol className="ion-text-right" size="6">
                        {addCurrency(+order?.shippingTotal!)}
                      </IonCol>
                    </IonRow>
                  )}
                  {addCurrency(+order?.discountTotal!) && (
                    <IonRow>
                      <IonCol size="6">{t("Discount")}</IonCol>
                      <IonCol className="ion-text-right" size="6">
                        {addCurrency(+order?.discountTotal!)}
                      </IonCol>
                    </IonRow>
                  )}

                  <IonRow>
                    <IonCol size="6">{t("Total")}</IonCol>
                    <IonCol className="ion-text-right" size="6">
                      <strong>{addCurrency(+order?.total!)}</strong>
                    </IonCol>
                  </IonRow>

                  {order?.driverRating && (
                    <IonRow>
                      <IonCol size="6">{t("Your rating for the driver")}</IonCol>
                      <IonCol className="ion-text-right" size="6">
                        <strong>{order.driverRating?.rating}</strong>
                      </IonCol>
                    </IonRow>
                  )}
                </IonCardContent>
              </IonCard>
              {order?.customerNote && (
                <IonCard>
                  <IonCardHeader color="primary" className={cx("order-details-header")}>
                    <IonCardTitle className={cx("order-details-title")}>{t("Order Notes")}</IonCardTitle>
                  </IonCardHeader>

                  <IonCardContent
                    className={cx("ion-no-padding ion-margin-top", "order-details-description", "order-details-note")}
                  >
                    <p className={cx("order-details-description")}>{order.customerNote}</p>
                  </IonCardContent>
                </IonCard>
              )}
            </MaxWidthContainer>
          )}
        </div>
        <Page.ContentFooter />
      </Page.Content>
    </Page>
  );
};

export default MyOrderDetailFortime;
